/**
 * Field schemas for every source, effect and instrument type in
 * engine/docs/systems/sound.md, plus reference lists (General MIDI programs
 * and drum keys, IPA symbols). The editors render these generically; keys
 * with structured values (operators, partials, bands, nested layers, notes)
 * get custom editors.
 */

export type FieldSpec = {
  key: string;
  label?: string;
  hint?: string;
  /** Only show when this returns true for the owning object. */
  when?: (obj: Record<string, unknown>) => boolean;
} & (
  | { kind: 'param'; step?: number; min?: number; max?: number; pitch?: boolean; placeholder?: number }
  | { kind: 'number'; step?: number; min?: number; max?: number; integer?: boolean; placeholder?: number }
  | { kind: 'select'; options: readonly string[]; placeholder?: string }
  | { kind: 'bool' }
  | { kind: 'text'; placeholder?: string; mono?: boolean; suggestions?: readonly string[] }
  | { kind: 'path'; placeholder?: string; ext?: RegExp }
  | { kind: 'json' }
  | { kind: 'custom' }
);

const FREQ: FieldSpec[] = [
  { key: 'note', kind: 'param', pitch: true, step: 1, hint: 'Pitch: MIDI number, note name ("A4") or { hz }' },
  { key: 'freq', kind: 'param', step: 1, min: 0, hint: 'Frequency in Hz (alternative to note)' },
];

export const MATERIALS = ['wood', 'metal', 'glass', 'stone'] as const;

export const SOURCE_TYPES = [
  'osc',
  'fm',
  'wavetable',
  'additive',
  'noise',
  'granular',
  'sample',
  'pluck',
  'string',
  'membrane',
  'bar',
  'tube',
  'impact',
  'chirp',
  'impulse',
  'instrument',
  'speech',
  'mix',
] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];

export const SOURCE_LABELS: Record<SourceType, string> = {
  osc: 'Oscillator',
  fm: 'FM synth',
  wavetable: 'Wavetable',
  additive: 'Additive',
  noise: 'Noise',
  granular: 'Granular',
  sample: 'Sample',
  pluck: 'Pluck (Karplus-Strong)',
  string: 'String (waveguide)',
  membrane: 'Membrane (drum)',
  bar: 'Bar / bell (modal)',
  tube: 'Tube (wind / brass)',
  impact: 'Impact',
  chirp: 'Chirp (sweep)',
  impulse: 'Impulse',
  instrument: 'Instrument notes',
  speech: 'Speech',
  mix: 'Group (nested layers)',
};

export const SOURCE_FIELDS: Record<SourceType, FieldSpec[]> = {
  osc: [
    { key: 'wave', kind: 'select', options: ['sine', 'triangle', 'saw', 'square', 'pulse', 'supersaw'], placeholder: 'sine' },
    ...FREQ,
    { key: 'width', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5, hint: 'Pulse width', when: (o) => o.wave === 'pulse' },
    { key: 'voices', kind: 'number', integer: true, min: 1, max: 16, placeholder: 7, when: (o) => o.wave === 'supersaw' },
    { key: 'detune', kind: 'param', step: 0.01, min: 0, placeholder: 0.2, when: (o) => o.wave === 'supersaw' },
  ],
  fm: [
    ...FREQ,
    { key: 'carrier', kind: 'param', step: 0.1, hint: 'Carrier ratio / frequency' },
    { key: 'algorithm', kind: 'number', integer: true, min: 1, max: 32, hint: 'DX7-style algorithm 1..32' },
    { key: 'routing', kind: 'json', hint: 'Explicit operator graph [[from, to], ...] (overrides algorithm)' },
    { key: 'operators', kind: 'custom' },
  ],
  wavetable: [
    {
      key: 'table',
      kind: 'text',
      mono: true,
      placeholder: 'basic',
      hint: 'Built-in table name or a single-cycle *.wav set',
      suggestions: ['basic', 'pwm', 'harmonic', 'vocal', 'digital', 'metallic', 'organ', 'bell'],
    },
    { key: 'position', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0 },
    ...FREQ,
  ],
  additive: [...FREQ, { key: 'spectrum', kind: 'custom' }, { key: 'partials', kind: 'custom' }],
  noise: [
    { key: 'color', kind: 'select', options: ['white', 'pink', 'brown', 'blue', 'violet', 'velvet'], placeholder: 'white' },
    { key: 'filter', kind: 'json', hint: 'Optional filter shortcut (same fields as the filter effect)' },
  ],
  granular: [
    { key: 'sample', kind: 'path', ext: /\.(wav|ogg|mp3|flac)$/i },
    { key: 'grainSize', kind: 'param', step: 0.005, min: 0.001, placeholder: 0.05, hint: 'Seconds' },
    { key: 'density', kind: 'param', step: 1, min: 0, placeholder: 20, hint: 'Grains per second' },
    { key: 'position', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0 },
    { key: 'spread', kind: 'param', step: 0.01, min: 0, placeholder: 0.1 },
    { key: 'pitchJitter', kind: 'param', step: 0.1, min: 0, placeholder: 0, hint: 'Semitones' },
    { key: 'reverse', kind: 'bool' },
  ],
  sample: [
    { key: 'path', kind: 'path', ext: /\.(wav|ogg|mp3|flac)$/i },
    { key: 'start', kind: 'number', step: 0.01, min: 0, hint: 'Seconds' },
    { key: 'end', kind: 'number', step: 0.01, min: 0, hint: 'Seconds' },
    { key: 'loop', kind: 'bool' },
    { key: 'rate', kind: 'param', step: 0.01, min: 0, placeholder: 1 },
    { key: 'pitch', kind: 'param', step: 0.1, placeholder: 0, hint: 'Semitones' },
    { key: 'reverse', kind: 'bool' },
  ],
  pluck: [
    ...FREQ,
    { key: 'decay', kind: 'param', step: 0.05, min: 0, placeholder: 1, hint: 'Seconds' },
    { key: 'brightness', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5 },
    { key: 'pickPosition', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.2 },
    { key: 'stiffness', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0 },
  ],
  string: [
    ...FREQ,
    { key: 'excitation', kind: 'select', options: ['bow', 'pluck', 'hammer'], placeholder: 'bow' },
    { key: 'bowPressure', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5 },
    { key: 'damping', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.3 },
  ],
  membrane: [
    { key: 'size', kind: 'param', step: 0.01, min: 0, placeholder: 0.5 },
    { key: 'tension', kind: 'param', step: 0.01, min: 0, placeholder: 0.5 },
    { key: 'damping', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.3 },
    { key: 'strikePosition', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.3 },
    { key: 'modes', kind: 'number', integer: true, min: 1, max: 64, placeholder: 16 },
    ...FREQ,
  ],
  bar: [
    { key: 'material', kind: 'select', options: MATERIALS, placeholder: 'metal' },
    { key: 'size', kind: 'param', step: 0.01, min: 0, placeholder: 0.5 },
    { key: 'hardness', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5 },
    { key: 'modes', kind: 'number', integer: true, min: 1, max: 64, placeholder: 8 },
    ...FREQ,
  ],
  tube: [
    ...FREQ,
    { key: 'breath', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5 },
    { key: 'reed', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5 },
    { key: 'bore', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5 },
  ],
  impact: [
    { key: 'material', kind: 'select', options: MATERIALS, placeholder: 'wood' },
    { key: 'size', kind: 'param', step: 0.01, min: 0, placeholder: 0.5 },
    { key: 'hardness', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5 },
    { key: 'velocity', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.8 },
    {
      key: 'surface',
      kind: 'text',
      placeholder: 'concrete',
      suggestions: ['concrete', 'wood', 'metal', 'grass', 'gravel', 'dirt', 'sand', 'snow', 'water', 'tile', 'carpet'],
    },
  ],
  chirp: [
    { key: 'from', kind: 'param', step: 10, min: 0, placeholder: 2000, hint: 'Start frequency (Hz)' },
    { key: 'to', kind: 'param', step: 10, min: 0, placeholder: 200, hint: 'End frequency (Hz)' },
    { key: 'curve', kind: 'select', options: ['linear', 'exp', 'log'], placeholder: 'exp' },
  ],
  impulse: [],
  instrument: [{ key: 'instrument', kind: 'custom' }, { key: 'notes', kind: 'custom' }],
  speech: [
    { key: 'text', kind: 'text', placeholder: 'The gate is open.' },
    { key: 'voice', kind: 'custom' },
    { key: 'speed', kind: 'number', step: 0.05, min: 0.1, placeholder: 1 },
    { key: 'pitch', kind: 'number', step: 0.5, placeholder: 0, hint: 'Semitones' },
    { key: 'energy', kind: 'number', step: 0.05, min: 0, placeholder: 1 },
  ],
  mix: [{ key: 'layers', kind: 'custom' }],
};

/** Starting spec when a source type is picked. */
export const DEFAULT_SOURCES: Record<SourceType, Record<string, unknown>> = {
  osc: { type: 'osc', wave: 'saw', note: 'A3' },
  fm: { type: 'fm', note: 'A3', operators: [{ ratio: 1, index: 2 }] },
  wavetable: { type: 'wavetable', table: 'basic', position: 0, note: 'A3' },
  additive: { type: 'additive', note: 'A3', spectrum: 'harmonic' },
  noise: { type: 'noise', color: 'white' },
  granular: { type: 'granular', sample: '', grainSize: 0.05, density: 20 },
  sample: { type: 'sample', path: '' },
  pluck: { type: 'pluck', note: 'E3', decay: 1.5, brightness: 0.6 },
  string: { type: 'string', note: 'G3', excitation: 'bow' },
  membrane: { type: 'membrane', size: 0.5, tension: 0.5, damping: 0.3 },
  bar: { type: 'bar', material: 'metal', note: 'C5' },
  tube: { type: 'tube', note: 'C4', breath: 0.6 },
  impact: { type: 'impact', material: 'wood', size: 0.4, hardness: 0.5, velocity: 0.8 },
  chirp: { type: 'chirp', from: 2000, to: 200, curve: 'exp' },
  impulse: { type: 'impulse' },
  instrument: { type: 'instrument', instrument: '', notes: [{ t: 0, d: 1, p: 'C4', v: 0.8 }] },
  speech: { type: 'speech', text: 'Hello.', voice: 'en_US-amy-medium' },
  mix: { type: 'mix', layers: [] },
};

/* ------------------------------------------------------------------ */
/* Effects                                                             */

export const EFFECT_TYPES = [
  'gain',
  'pan',
  'width',
  'eq',
  'filter',
  'comb',
  'formant',
  'distortion',
  'compressor',
  'limiter',
  'gate',
  'transient',
  'chorus',
  'flanger',
  'phaser',
  'tremolo',
  'vibrato',
  'ringmod',
  'delay',
  'reverb',
  'convolution',
  'pitchShift',
  'timeStretch',
  'reverse',
  'fade',
  'doppler',
  'spatial',
] as const;
export type EffectType = (typeof EFFECT_TYPES)[number];

export const EFFECT_GROUPS: { label: string; types: EffectType[] }[] = [
  { label: 'Level & stereo', types: ['gain', 'pan', 'width', 'spatial', 'doppler'] },
  { label: 'Tone', types: ['eq', 'filter', 'comb', 'formant'] },
  { label: 'Drive', types: ['distortion', 'ringmod'] },
  { label: 'Dynamics', types: ['compressor', 'limiter', 'gate', 'transient'] },
  { label: 'Modulation', types: ['chorus', 'flanger', 'phaser', 'tremolo', 'vibrato'] },
  { label: 'Time & space', types: ['delay', 'reverb', 'convolution'] },
  { label: 'Pitch & time', types: ['pitchShift', 'timeStretch', 'reverse', 'fade'] },
];

const RATE: FieldSpec = { key: 'rate', kind: 'param', step: 0.1, min: 0, placeholder: 1, hint: 'Hz' };
const SYNC: FieldSpec = { key: 'sync', kind: 'select', options: ['1/1', '1/2', '1/4', '1/8', '1/16', '1/32', '1/4t', '1/8t', '1/16t', '1/4.', '1/8.'], placeholder: 'Free' };

export const EFFECT_FIELDS: Record<EffectType, FieldSpec[]> = {
  gain: [{ key: 'gain', kind: 'param', step: 0.5, placeholder: 0, hint: 'dB' }],
  pan: [{ key: 'pan', kind: 'param', step: 0.05, min: -1, max: 1, placeholder: 0 }],
  width: [{ key: 'width', kind: 'param', step: 0.05, min: 0, max: 2, placeholder: 1, hint: '0 = mono, 1 = unchanged, 2 = wide' }],
  eq: [{ key: 'bands', kind: 'custom' }],
  filter: [
    { key: 'mode', kind: 'select', options: ['lowpass', 'highpass', 'bandpass', 'notch', 'allpass'], placeholder: 'lowpass' },
    { key: 'cutoff', kind: 'param', step: 10, min: 10, max: 24000, placeholder: 1000, hint: 'Hz' },
    { key: 'resonance', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.2 },
    { key: 'slope', kind: 'select', options: ['12', '24', '48'], placeholder: '12', hint: 'dB / octave' },
    { key: 'drive', kind: 'param', step: 0.05, min: 0, placeholder: 0 },
    { key: 'model', kind: 'select', options: ['svf', 'ladder'], placeholder: 'svf' },
  ],
  comb: [
    { key: 'freq', kind: 'param', step: 1, min: 1, placeholder: 200, hint: 'Hz (or `time` in seconds)' },
    { key: 'feedback', kind: 'param', step: 0.01, min: -0.999, max: 0.999, placeholder: 0.7 },
    { key: 'damping', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.2 },
  ],
  formant: [
    { key: 'vowel', kind: 'select', options: ['a', 'e', 'i', 'o', 'u'], placeholder: 'a' },
    { key: 'formants', kind: 'json', hint: 'Explicit formants [{ freq, q, gain }] (overrides vowel)' },
    { key: 'shift', kind: 'param', step: 0.5, placeholder: 0, hint: 'Semitones' },
  ],
  distortion: [
    { key: 'curve', kind: 'select', options: ['soft', 'hard', 'tube', 'fold', 'bitcrush'], placeholder: 'soft' },
    { key: 'drive', kind: 'param', step: 0.5, min: 0, placeholder: 6, hint: 'dB' },
    { key: 'bits', kind: 'number', integer: true, min: 1, max: 24, placeholder: 8, when: (o) => o.curve === 'bitcrush' },
    { key: 'downsample', kind: 'number', integer: true, min: 1, max: 64, placeholder: 1, when: (o) => o.curve === 'bitcrush' },
    { key: 'output', kind: 'param', step: 0.5, placeholder: 0, hint: 'Output gain dB' },
  ],
  compressor: [
    { key: 'threshold', kind: 'param', step: 0.5, max: 0, placeholder: -18, hint: 'dB' },
    { key: 'ratio', kind: 'param', step: 0.5, min: 1, placeholder: 4 },
    { key: 'attack', kind: 'number', step: 0.001, min: 0, placeholder: 0.01, hint: 'Seconds' },
    { key: 'release', kind: 'number', step: 0.01, min: 0, placeholder: 0.15, hint: 'Seconds' },
    { key: 'knee', kind: 'number', step: 0.5, min: 0, placeholder: 6, hint: 'dB' },
    { key: 'makeup', kind: 'param', step: 0.5, placeholder: 0, hint: 'dB' },
    { key: 'sidechain', kind: 'custom' },
  ],
  limiter: [
    { key: 'ceiling', kind: 'number', step: 0.1, max: 0, placeholder: -1, hint: 'dBTP' },
    { key: 'release', kind: 'number', step: 0.01, min: 0, placeholder: 0.05, hint: 'Seconds' },
  ],
  gate: [
    { key: 'threshold', kind: 'param', step: 0.5, max: 0, placeholder: -40, hint: 'dB' },
    { key: 'attack', kind: 'number', step: 0.001, min: 0, placeholder: 0.001 },
    { key: 'hold', kind: 'number', step: 0.005, min: 0, placeholder: 0.02 },
    { key: 'release', kind: 'number', step: 0.01, min: 0, placeholder: 0.1 },
    { key: 'range', kind: 'number', step: 1, max: 0, placeholder: -80, hint: 'dB' },
  ],
  transient: [
    { key: 'attack', kind: 'param', step: 0.05, min: -1, max: 1, placeholder: 0, hint: '-1..1 (soften .. punch)' },
    { key: 'sustain', kind: 'param', step: 0.05, min: -1, max: 1, placeholder: 0 },
  ],
  chorus: [RATE, { key: 'depth', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.3 }, { key: 'voices', kind: 'number', integer: true, min: 1, max: 8, placeholder: 3 }, { key: 'delay', kind: 'number', step: 0.001, min: 0, placeholder: 0.015, hint: 'Seconds' }],
  flanger: [RATE, { key: 'depth', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5 }, { key: 'feedback', kind: 'param', step: 0.01, min: -0.99, max: 0.99, placeholder: 0.5 }],
  phaser: [RATE, { key: 'depth', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5 }, { key: 'stages', kind: 'number', integer: true, min: 2, max: 12, placeholder: 4 }, { key: 'feedback', kind: 'param', step: 0.01, min: -0.99, max: 0.99, placeholder: 0.3 }],
  tremolo: [RATE, SYNC, { key: 'depth', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5 }, { key: 'shape', kind: 'select', options: ['sine', 'triangle', 'square', 'saw'], placeholder: 'sine' }],
  vibrato: [RATE, { key: 'depth', kind: 'param', step: 0.05, min: 0, placeholder: 0.3, hint: 'Semitones' }],
  ringmod: [{ key: 'freq', kind: 'param', step: 1, min: 0, placeholder: 30, hint: 'Hz' }],
  delay: [
    { key: 'time', kind: 'param', step: 0.01, min: 0, placeholder: 0.25, hint: 'Seconds (or sync)' },
    SYNC,
    { key: 'feedback', kind: 'param', step: 0.01, min: 0, max: 0.99, placeholder: 0.35 },
    { key: 'pingPong', kind: 'bool' },
    { key: 'filter', kind: 'json', hint: 'Feedback filter (filter effect fields)' },
  ],
  reverb: [
    { key: 'size', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5 },
    { key: 'decay', kind: 'param', step: 0.1, min: 0, placeholder: 1.5, hint: 'Seconds' },
    { key: 'damping', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.5 },
    { key: 'predelay', kind: 'number', step: 0.005, min: 0, placeholder: 0.02, hint: 'Seconds' },
    { key: 'diffusion', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.7 },
    { key: 'early', kind: 'param', step: 0.01, min: 0, max: 1, placeholder: 0.3, hint: 'Early reflections level' },
  ],
  convolution: [
    {
      key: 'ir',
      kind: 'text',
      mono: true,
      placeholder: 'hall',
      hint: 'Impulse response: built-in name or a *.wav path',
      suggestions: ['room', 'hall', 'plate', 'spring', 'cave'],
    },
  ],
  pitchShift: [
    { key: 'semitones', kind: 'param', step: 0.5, placeholder: 0 },
    { key: 'formant', kind: 'bool', label: 'Keep formants' },
  ],
  timeStretch: [{ key: 'factor', kind: 'number', step: 0.05, min: 0.05, placeholder: 1 }],
  reverse: [],
  fade: [
    { key: 'in', kind: 'number', step: 0.005, min: 0, placeholder: 0, hint: 'Seconds' },
    { key: 'out', kind: 'number', step: 0.005, min: 0, placeholder: 0, hint: 'Seconds' },
  ],
  doppler: [{ key: 'path', kind: 'json', hint: 'Source motion: envelope points or [[t, x, y], ...]' }],
  spatial: [
    { key: 'azimuth', kind: 'param', step: 1, min: -180, max: 180, placeholder: 0, hint: 'Degrees' },
    { key: 'distance', kind: 'param', step: 0.1, min: 0, placeholder: 1 },
  ],
};

export const DEFAULT_EFFECTS: Partial<Record<EffectType, Record<string, unknown>>> = {
  eq: { type: 'eq', bands: [{ type: 'peak', freq: 1000, q: 1, gain: 0 }] },
  filter: { type: 'filter', mode: 'lowpass', cutoff: 2000, resonance: 0.2 },
  reverb: { type: 'reverb', size: 0.6, decay: 1.8, mix: 0.3 },
  delay: { type: 'delay', time: 0.25, feedback: 0.35, mix: 0.3 },
  distortion: { type: 'distortion', curve: 'soft', drive: 6 },
  compressor: { type: 'compressor', threshold: -18, ratio: 4 },
  chorus: { type: 'chorus', rate: 0.8, depth: 0.3, mix: 0.5 },
  convolution: { type: 'convolution', ir: 'hall', mix: 0.3 },
};

export const EQ_BAND_TYPES = ['lowshelf', 'highshelf', 'peak', 'lowpass', 'highpass', 'bandpass', 'notch'] as const;

/* ------------------------------------------------------------------ */
/* Instruments                                                         */

export const INSTRUMENT_TYPES = ['soundfont', 'synth', 'drumkit', 'sampler', 'singer', 'ref'] as const;
export type InstrumentType = (typeof INSTRUMENT_TYPES)[number];

export const DEFAULT_INSTRUMENTS: Record<InstrumentType, Record<string, unknown>> = {
  soundfont: { type: 'soundfont', bank: 0, program: 0 },
  synth: { type: 'synth', voices: 8, source: { type: 'osc', wave: 'saw' }, amp: { adsr: [0.01, 0.2, 0.7, 0.4] } },
  drumkit: { type: 'drumkit', pads: {} },
  sampler: { type: 'sampler', zones: [] },
  singer: { type: 'singer', voice: 'soprano' },
  ref: { type: 'ref', path: '' },
};

export const SINGER_VOICES = ['soprano', 'alto', 'tenor', 'bass', 'child', 'robot'] as const;

/** General MIDI level 1 program names, index = program number (0-based). */
export const GM_PROGRAMS: readonly string[] = [
  'Acoustic Grand Piano', 'Bright Acoustic Piano', 'Electric Grand Piano', 'Honky-tonk Piano', 'Electric Piano 1', 'Electric Piano 2', 'Harpsichord', 'Clavinet',
  'Celesta', 'Glockenspiel', 'Music Box', 'Vibraphone', 'Marimba', 'Xylophone', 'Tubular Bells', 'Dulcimer',
  'Drawbar Organ', 'Percussive Organ', 'Rock Organ', 'Church Organ', 'Reed Organ', 'Accordion', 'Harmonica', 'Tango Accordion',
  'Acoustic Guitar (nylon)', 'Acoustic Guitar (steel)', 'Electric Guitar (jazz)', 'Electric Guitar (clean)', 'Electric Guitar (muted)', 'Overdriven Guitar', 'Distortion Guitar', 'Guitar Harmonics',
  'Acoustic Bass', 'Electric Bass (finger)', 'Electric Bass (pick)', 'Fretless Bass', 'Slap Bass 1', 'Slap Bass 2', 'Synth Bass 1', 'Synth Bass 2',
  'Violin', 'Viola', 'Cello', 'Contrabass', 'Tremolo Strings', 'Pizzicato Strings', 'Orchestral Harp', 'Timpani',
  'String Ensemble 1', 'String Ensemble 2', 'Synth Strings 1', 'Synth Strings 2', 'Choir Aahs', 'Voice Oohs', 'Synth Voice', 'Orchestra Hit',
  'Trumpet', 'Trombone', 'Tuba', 'Muted Trumpet', 'French Horn', 'Brass Section', 'Synth Brass 1', 'Synth Brass 2',
  'Soprano Sax', 'Alto Sax', 'Tenor Sax', 'Baritone Sax', 'Oboe', 'English Horn', 'Bassoon', 'Clarinet',
  'Piccolo', 'Flute', 'Recorder', 'Pan Flute', 'Blown Bottle', 'Shakuhachi', 'Whistle', 'Ocarina',
  'Lead 1 (square)', 'Lead 2 (sawtooth)', 'Lead 3 (calliope)', 'Lead 4 (chiff)', 'Lead 5 (charang)', 'Lead 6 (voice)', 'Lead 7 (fifths)', 'Lead 8 (bass + lead)',
  'Pad 1 (new age)', 'Pad 2 (warm)', 'Pad 3 (polysynth)', 'Pad 4 (choir)', 'Pad 5 (bowed)', 'Pad 6 (metallic)', 'Pad 7 (halo)', 'Pad 8 (sweep)',
  'FX 1 (rain)', 'FX 2 (soundtrack)', 'FX 3 (crystal)', 'FX 4 (atmosphere)', 'FX 5 (brightness)', 'FX 6 (goblins)', 'FX 7 (echoes)', 'FX 8 (sci-fi)',
  'Sitar', 'Banjo', 'Shamisen', 'Koto', 'Kalimba', 'Bagpipe', 'Fiddle', 'Shanai',
  'Tinkle Bell', 'Agogo', 'Steel Drums', 'Woodblock', 'Taiko Drum', 'Melodic Tom', 'Synth Drum', 'Reverse Cymbal',
  'Guitar Fret Noise', 'Breath Noise', 'Seashore', 'Bird Tweet', 'Telephone Ring', 'Helicopter', 'Applause', 'Gunshot',
];

export const GM_FAMILIES: readonly string[] = [
  'Piano', 'Chromatic Percussion', 'Organ', 'Guitar', 'Bass', 'Strings', 'Ensemble', 'Brass',
  'Reed', 'Pipe', 'Synth Lead', 'Synth Pad', 'Synth Effects', 'Ethnic', 'Percussive', 'Sound Effects',
];

/** General MIDI percussion key map (channel 10 / bank 128). */
export const GM_DRUMS: Readonly<Record<number, string>> = {
  35: 'Acoustic Bass Drum', 36: 'Bass Drum 1', 37: 'Side Stick', 38: 'Acoustic Snare', 39: 'Hand Clap', 40: 'Electric Snare',
  41: 'Low Floor Tom', 42: 'Closed Hi-Hat', 43: 'High Floor Tom', 44: 'Pedal Hi-Hat', 45: 'Low Tom', 46: 'Open Hi-Hat',
  47: 'Low-Mid Tom', 48: 'Hi-Mid Tom', 49: 'Crash Cymbal 1', 50: 'High Tom', 51: 'Ride Cymbal 1', 52: 'Chinese Cymbal',
  53: 'Ride Bell', 54: 'Tambourine', 55: 'Splash Cymbal', 56: 'Cowbell', 57: 'Crash Cymbal 2', 58: 'Vibraslap',
  59: 'Ride Cymbal 2', 60: 'Hi Bongo', 61: 'Low Bongo', 62: 'Mute Hi Conga', 63: 'Open Hi Conga', 64: 'Low Conga',
  65: 'High Timbale', 66: 'Low Timbale', 67: 'High Agogo', 68: 'Low Agogo', 69: 'Cabasa', 70: 'Maracas',
  71: 'Short Whistle', 72: 'Long Whistle', 73: 'Short Guiro', 74: 'Long Guiro', 75: 'Claves', 76: 'Hi Wood Block',
  77: 'Low Wood Block', 78: 'Mute Cuica', 79: 'Open Cuica', 80: 'Mute Triangle', 81: 'Open Triangle',
};

/** The 16 pads shown first in the drum kit grid (bottom-left = kick, like an MPC). */
export const DRUM_PAD_KEYS: readonly number[] = [49, 57, 51, 55, 45, 47, 48, 50, 42, 44, 46, 54, 36, 38, 40, 39];

/* ------------------------------------------------------------------ */
/* Voice                                                               */

/** IPA symbols (espeak-ng / Piper phoneme set) with an example word. */
export const IPA_GROUPS: { label: string; symbols: [string, string][] }[] = [
  {
    label: 'Vowels',
    symbols: [
      ['i', 'see'], ['ɪ', 'sit'], ['e', 'bed (FR é)'], ['ɛ', 'bed'], ['æ', 'cat'], ['a', 'father (short)'], ['ɑ', 'father'],
      ['ɒ', 'lot (UK)'], ['ɔ', 'thought'], ['o', 'go (FR)'], ['ʊ', 'put'], ['u', 'too'], ['ʌ', 'cup'], ['ə', 'about'],
      ['ɚ', 'butter (US)'], ['ɜ', 'bird'], ['ɝ', 'bird (US)'], ['y', 'FR tu'], ['ø', 'FR peu'],
    ],
  },
  {
    label: 'Diphthongs',
    symbols: [['eɪ', 'say'], ['aɪ', 'my'], ['ɔɪ', 'boy'], ['aʊ', 'now'], ['oʊ', 'go'], ['ɪə', 'near (UK)'], ['eə', 'hair (UK)']],
  },
  {
    label: 'Consonants',
    symbols: [
      ['p', 'pen'], ['b', 'bad'], ['t', 'tea'], ['d', 'did'], ['k', 'cat'], ['ɡ', 'get'], ['tʃ', 'chain'], ['dʒ', 'jam'],
      ['f', 'fall'], ['v', 'van'], ['θ', 'thin'], ['ð', 'this'], ['s', 'see'], ['z', 'zoo'], ['ʃ', 'shoe'], ['ʒ', 'vision'],
      ['h', 'hat'], ['m', 'man'], ['n', 'no'], ['ŋ', 'sing'], ['l', 'leg'], ['ɹ', 'red'], ['r', 'trilled r'], ['ɾ', 'butter (US flap)'],
      ['j', 'yes'], ['w', 'wet'], ['ʔ', 'uh-oh'], ['x', 'loch'],
    ],
  },
  {
    label: 'Marks',
    symbols: [['ˈ', 'primary stress'], ['ˌ', 'secondary stress'], ['ː', 'long'], ['.', 'syllable break'], [' ', 'word break']],
  },
];
